// Data validation and transformation utilities for bulk upload
// This module transforms CSV data to match the EXACT API format used by individual student registration
// API Endpoint: /api/v1/Staff/AddEditStaff (proxied from https://bus.little.africa/server/api/v1/Student/AddEditStudent)

import { mapDataToFields } from './fileParser';
import { getCoordinatesForStopAsync } from './geoLookup';

/**
 * Valid class formats that the API accepts
 * These are mapped to numeric codes for the classstandards field
 */
const VALID_CLASS_FORMATS = [
  // Preschool levels
  'Playgroup', 'PP1', 'PP2',
  // Primary and secondary grades
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
  'Form 1', 'Form 2', 'Form 3', 'Form 4',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'
];


const CLASS_TO_CODE = {
  // Preschool levels
  'Playgroup': 0, 'PP1': 1, 'PP2': 2,
  // Primary and secondary grades
  'Grade 1': 3, 'Grade 2': 4, 'Grade 3': 5, 'Grade 4': 6, 'Grade 5': 7,
  'Grade 6': 8, 'Grade 7': 9, 'Grade 8': 10, 'Grade 9': 11, 'Grade 10': 12,
  'Grade 11': 13, 'Grade 12': 14,
  
  'Class 1': 15, 'Class 2': 16, 'Class 3': 17, 'Class 4': 18, 'Class 5': 19,
  'Class 6': 20, 'Class 7': 21, 'Class 8': 22,
  'Form 1': 23, 'Form 2': 24, 'Form 3': 25, 'Form 4': 26,
};

/**
 * Validates that a name contains only letters and spaces (max 15 chars)
 */
const isValidName = (value, maxLen = 15) => {
  const v = String(value || '').trim();
  if (!v) return false;
  const re = new RegExp(`^[a-zA-Z ]{1,${maxLen}}$`);
  return re.test(v);
};


/**
 * Validates email format
 */
const isValidEmail = (value) => {
  const v = String(value || '').trim();
  if (!v) return true; // Email is optional
  return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(v);
};

/**
 * Extracts only digits from a value
 */
const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

/**
 * Validates that a mobile number has exactly 9 digits (without country code)
 */
const isValidMobile = (value) => {
  const digits = digitsOnly(value);
  return digits.length >= 9 && digits.length <= 10;
};

/**
 * Validates that latitude/longitude values are valid numbers
 */
const isValidCoordinate = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num);
};

/**
 * Generate actionable error message for invalid names
 * @param {string} fieldLabel - Human-readable field name (e.g., "Student First Name")
 * @param {string} value - The invalid value entered
 * @returns {string} - Actionable error message
 */
const getNameErrorMessage = (fieldLabel, value) => {
  const trimmed = String(value || '').trim();
  
  if (!trimmed) {
    return `${fieldLabel} is empty. Please enter a name using only letters (e.g., "John")`;
  }
  
  // Check for numbers in the name
  const hasNumbers = /\d/.test(trimmed);
  if (hasNumbers) {
    // Suggest the name without numbers
    const suggestedName = trimmed.replace(/\d/g, '').trim();
    if (suggestedName) {
      return `Change "${trimmed}" to "${suggestedName}" — numbers aren't allowed in ${fieldLabel.toLowerCase()}`;
    }
    return `"${trimmed}" contains numbers. ${fieldLabel} must only contain letters (e.g., "John")`;
  }
  
  // Check for special characters
  const hasSpecialChars = /[^a-zA-Z\s]/.test(trimmed);
  if (hasSpecialChars) {
    const suggestedName = trimmed.replace(/[^a-zA-Z\s]/g, '').trim();
    if (suggestedName) {
      return `Change "${trimmed}" to "${suggestedName}" — special characters aren't allowed in ${fieldLabel.toLowerCase()}`;
    }
    return `"${trimmed}" contains special characters. ${fieldLabel} must only contain letters`;
  }
  
  // Check if too long
  if (trimmed.length > 15) {
    const suggestedName = trimmed.substring(0, 15).trim();
    return `"${trimmed}" is too long (${trimmed.length} chars). Shorten to "${suggestedName}" (max 15 characters)`;
  }
  
  return `"${trimmed}" is invalid for ${fieldLabel}. Please use only letters and spaces (max 15 characters)`;
};

/**
 * Generate actionable error message for invalid mobile numbers
 * @param {string} value - The invalid value entered
 * @returns {string} - Actionable error message
 */
const getMobileErrorMessage = (value) => {
  const trimmed = String(value || '').trim();
  const digits = digitsOnly(trimmed);
  
  if (!trimmed || !digits) {
    return 'Parent Mobile is empty. Please enter a 9-digit phone number (e.g., "712345678")';
  }
  
  if (digits.length < 9) {
    return `"${trimmed}" is too short (${digits.length} digits). Phone number must be 9-10 digits — try adding ${9 - digits.length} more digit(s)`;
  }
  
  if (digits.length > 10) {
    // Check if it starts with country code
    if (digits.startsWith('254') && digits.length >= 12) {
      const withoutCode = digits.substring(3);
      return `"${trimmed}" includes country code. Remove "254" and use just "${withoutCode}" (9 digits without country code)`;
    }
    if (digits.startsWith('0') && digits.length === 10) {
      const withoutZero = digits.substring(1);
      return `"${trimmed}" starts with 0. Remove the leading zero and use "${withoutZero}"`;
    }
    return `"${trimmed}" is too long (${digits.length} digits). Phone number must be 9-10 digits without country code`;
  }
  
  return `"${trimmed}" is invalid. Enter 9-10 digits without country code (e.g., "712345678")`;
};

/**
 * Generate actionable error message for invalid email
 * @param {string} value - The invalid value entered
 * @returns {string} - Actionable error message
 */
const getEmailErrorMessage = (value) => {
  const trimmed = String(value || '').trim();
  
  if (!trimmed.includes('@')) {
    return `"${trimmed}" is missing @ symbol. Email should be like "name@example.com"`;
  }
  
  if (!trimmed.includes('.')) {
    return `"${trimmed}" is missing domain extension. Email should be like "name@example.com"`;
  }
  
  if (trimmed.includes(' ')) {
    const suggested = trimmed.replace(/\s/g, '');
    return `"${trimmed}" contains spaces. Remove spaces: "${suggested}"`;
  }
  
  return `"${trimmed}" is not a valid email format. Use format: "name@example.com"`;
};

/**
 * Generate actionable error message for invalid class
 * @param {string} value - The invalid value entered
 * @returns {string} - Actionable error message
 */
const getClassErrorMessage = (value) => {
  const trimmed = String(value || '').trim();
  
  if (!trimmed) {
    return 'Class is empty. Please enter a class like "Playgroup", "PP1", "PP2", "Grade 5", "Form 2", or "Class 8"';
  }
  
  // Try to suggest a correction
  const lowerValue = trimmed.toLowerCase();
  
  // Check for preschool variations
  if (lowerValue.includes('play') || lowerValue.includes('pg')) {
    return `Change "${trimmed}" to "Playgroup" — use the exact format "Playgroup"`;
  }
  
  // Check for PP1/PP2 variations
  const ppMatch = lowerValue.match(/p\.?p\.?[\s-]*([12])/i);
  if (ppMatch) {
    return `Change "${trimmed}" to "PP${ppMatch[1]}" — use the format "PP1" or "PP2"`;
  }
  
  // Check for just a number
  const justNumber = trimmed.match(/^(\d+)$/);
  if (justNumber) {
    const num = parseInt(justNumber[1]);
    if (num >= 1 && num <= 12) {
      return `Change "${trimmed}" to "Grade ${num}" — class needs a prefix like "Grade", "Form", or "Class"`;
    }
  }
  
  // Check for common misspellings or formats
  const gradeMatch = lowerValue.match(/gr(?:ade)?[\s-]*(\d+)/i);
  if (gradeMatch) {
    const num = parseInt(gradeMatch[1]);
    if (num >= 1 && num <= 12) {
      return `Change "${trimmed}" to "Grade ${num}" — use the format "Grade X"`;
    }
  }
  
  const formMatch = lowerValue.match(/f(?:orm)?[\s-]*(\d+)/i);
  if (formMatch) {
    const num = parseInt(formMatch[1]);
    if (num >= 1 && num <= 4) {
      return `Change "${trimmed}" to "Form ${num}" — use the format "Form X"`;
    }
  }
  
  const classMatch = lowerValue.match(/c(?:lass)?[\s-]*(\d+)/i);
  if (classMatch) {
    const num = parseInt(classMatch[1]);
    if (num >= 1 && num <= 10) {
      return `Change "${trimmed}" to "Class ${num}" — use the format "Class X"`;
    }
  }
  
  return `"${trimmed}" is not a valid class format. Use: "Playgroup", "PP1", "PP2", "Grade 1-12", "Form 1-4", or "Class 1-10"`;
};

/**
 * Generate actionable error message for invalid coordinates
 * @param {string} fieldLabel - "Pickup From Latitude", "Drop To Longitude", etc.
 * @param {string} value - The invalid value entered
 * @returns {string} - Actionable error message
 */
const getCoordinateErrorMessage = (fieldLabel, value) => {
  const trimmed = String(value || '').trim();
  
  if (!trimmed) {
    return `${fieldLabel} is empty. Enter a number like "-1.2921" for Nairobi coordinates`;
  }
  
  // Check for common issues
  if (trimmed.includes(',')) {
    return `"${trimmed}" contains a comma. Use a decimal point instead: "${trimmed.replace(',', '.')}"`;
  }
  
  if (trimmed.includes('°') || trimmed.includes("'") || trimmed.includes('"')) {
    return `"${trimmed}" uses degrees format. Convert to decimal format (e.g., "-1.2921" for latitude)`;
  }
  
  const num = parseFloat(trimmed);
  if (isNaN(num)) {
    return `"${trimmed}" is not a valid number for ${fieldLabel}. Use decimal format (e.g., "-1.2921")`;
  }
  
  return `"${trimmed}" is invalid for ${fieldLabel}. Enter coordinates in decimal format`;
};

/**
 * Generate actionable error message for missing location coordinates
 * @param {string} locationField - "Pickup From", "Drop To", etc.
 * @param {string} locationValue - The location name entered
 * @returns {string} - Actionable error message
 */
const getLocationCoordinateErrorMessage = (locationField, locationValue) => {
  const trimmed = String(locationValue || '').trim();
  
  if (!trimmed) {
    return `${locationField} is empty. Please enter a location name (e.g., "Yaya Centre", "Westlands")`;
  }
  
  return `Could not find coordinates for ${locationField} "${trimmed}". The location was not found in our database or via Google Maps. Try using a more specific address or a well-known landmark in Nairobi (e.g., "Yaya Centre", "Sarit Centre", "Karen Hub")`;
};

/**
 * Validate bulk data using the same rules as individual registration
 * NEW STRUCTURE: Uses Pickup From/To and Drop From/To instead of Pickup Stop/Drop Stop
 * Coordinates are looked up automatically for all four location fields
 * 
 * This function is now ASYNC to support Google Maps API fallback for unknown locations.
 * 
 * @param {Array} data - Parsed CSV data array
 * @param {string} type - 'Student' or 'Employee'
 * @returns {Promise<Array>} - Array of validated records with errors and auto-filled coordinates
 */
export const validateBulkData = async (data, type) => {
  const mappedData = mapDataToFields(data, type);

  // Process all records in parallel for better performance
  const results = await Promise.all(mappedData.map(async (item) => {
    const errors = [];
    const { data: rowData } = item;

    if (type === 'Student') {
      // Student First Name (required)
      if (!isValidName(rowData.studentFirstName)) {
        errors.push(getNameErrorMessage('Student First Name', rowData.studentFirstName));
      }

      // Student Last Name (required)
      if (!isValidName(rowData.studentLastName)) {
        errors.push(getNameErrorMessage('Student Last Name', rowData.studentLastName));
      }

      // Class (required, must be valid format)
      if (!rowData.class || !VALID_CLASS_FORMATS.includes(rowData.class)) {
        errors.push(getClassErrorMessage(rowData.class));
      }

      // Parent First Name (required)
      if (!isValidName(rowData.parentFirstName)) {
        errors.push(getNameErrorMessage('Parent First Name', rowData.parentFirstName));
      }

      // Parent Last Name (required)
      if (!isValidName(rowData.parentLastName)) {
        errors.push(getNameErrorMessage('Parent Last Name', rowData.parentLastName));
      }

      // Parent Mobile (required, 9 digits without country code)
      if (!isValidMobile(rowData.parentMobile)) {
        errors.push(getMobileErrorMessage(rowData.parentMobile));
      }

      // Parent Email (optional but must be valid if provided)
      if (rowData.parentEmail && !isValidEmail(rowData.parentEmail)) {
        errors.push(getEmailErrorMessage(rowData.parentEmail));
      }

      // Address (required)
      if (!rowData.address || !rowData.address.trim()) {
        errors.push('Address is empty. Please enter the home address (e.g., "House 45, Lavington")');
      }

      // Area (required)
      if (!rowData.area || !rowData.area.trim()) {
        errors.push('Area is empty. Please enter the neighborhood/area (e.g., "Westlands", "Karen", "Kilimani")');
      }

      // City (required)
      if (!rowData.city || !rowData.city.trim()) {
        errors.push('City is empty. Please enter the city name (e.g., "Nairobi")');
      }

      // ===================================================================
      // NEW LOCATION STRUCTURE: Pickup From/To and Drop From/To
      // Matching individual registration form:
      // - Morning Pickup: "From" (home) → "To" (school)
      // - Afternoon Drop: "From" (school) → "To" (home)
      //
      // IMPORTANT: The API only needs coordinates for the HOME location:
      // - pickupLL = Pickup From coordinates (student's home for morning pickup)
      // - dropLL = Drop To coordinates (student's home for afternoon drop)
      // The school coordinates (Pickup To, Drop From) are NOT needed by the API
      // because the school location is the same for all students.
      // ===================================================================

      // Pickup From (required - user enters home location for morning pickup)
      // This is the location where the bus picks up the student in the morning
      if (!rowData.pickupFrom || !rowData.pickupFrom.trim()) {
        errors.push('Pickup From is empty. Enter the morning pickup location (home area, e.g., "Yaya Centre", "Westlands")');
      } else {
        // Lookup coordinates for Pickup From - REQUIRED for API
        // Uses async function that falls back to Google Maps API if not in local cache
        const pickupFromCoords = await getCoordinatesForStopAsync(rowData.pickupFrom);
        if (pickupFromCoords) {
          rowData.pickupFromLat = pickupFromCoords.lat;
          rowData.pickupFromLng = pickupFromCoords.lng;
          rowData.pickupFromCoordsAutoFilled = true;
          rowData.pickupFromCoordsSource = pickupFromCoords.source || 'local'; // Track where coords came from
        } else {
          errors.push(getLocationCoordinateErrorMessage('Pickup From', rowData.pickupFrom));
        }
      }

      // Pickup To (required - school name, typically auto-filled from corpName)
      // Coordinates are NOT required - just the name for display purposes
      if (!rowData.pickupTo || !rowData.pickupTo.trim()) {
        errors.push('Pickup To is empty. Enter the school name (destination for morning pickup)');
      }
      // Note: We don't require coordinates for the school location

      // Drop From (required - school name, typically auto-filled from corpName)
      // Coordinates are NOT required - just the name for display purposes
      if (!rowData.dropFrom || !rowData.dropFrom.trim()) {
        errors.push('Drop From is empty. Enter the school name (origin for afternoon drop)');
      }
      // Note: We don't require coordinates for the school location

      // Drop To (required - user enters home location for afternoon drop)
      // This is the location where the bus drops off the student in the afternoon
      if (!rowData.dropTo || !rowData.dropTo.trim()) {
        errors.push('Drop To is empty. Enter the afternoon drop location (home area, e.g., "Lavington", "Karen")');
      } else {
        // Lookup coordinates for Drop To - REQUIRED for API
        // Uses async function that falls back to Google Maps API if not in local cache
        const dropToCoords = await getCoordinatesForStopAsync(rowData.dropTo);
        if (dropToCoords) {
          rowData.dropToLat = dropToCoords.lat;
          rowData.dropToLng = dropToCoords.lng;
          rowData.dropToCoordsAutoFilled = true;
          rowData.dropToCoordsSource = dropToCoords.source || 'local'; // Track where coords came from
        } else {
          errors.push(getLocationCoordinateErrorMessage('Drop To', rowData.dropTo));
        }
      }

      // Country Code (optional, defaults to 254 for Kenya)
      const countryCode = rowData.countryCode || '254';
      if (!['971', '254', '91'].includes(countryCode)) {
        errors.push(`"${countryCode}" is not a valid country code. Use: 254 (Kenya), 971 (UAE), or 91 (India)`);
      }
    }

    return {
      ...item,
      isValid: errors.length === 0,
      errors: errors
    };
  }));
  
  return results;
};

/**
 * Transform a single row of bulk data to match the EXACT API format
 * used by individual student registration
 * 
 * API Endpoint: /api/v1/Staff/AddEditStaff
 * 
 * NEW STRUCTURE: Uses Pickup From/To and Drop From/To fields
 * - Morning Pickup: "From" (home location) → "To" (school)
 * - Afternoon Drop: "From" (school) → "To" (home location)
 * 
 * The API expects pickupLL/dropLL to represent the student's home location:
 * - pickupLL: Where the bus picks up the student (home) - from Pickup From coords
 * - dropLL: Where the bus drops off the student (home) - from Drop To coords
 * - pickupStop: Full pickup location name (Pickup From location)
 * - dropStop: Full drop location name (Drop To location)
 * 
 * EXACT PAYLOAD FORMAT (from ReviewPage/index.js individual registration):
 * {
 *   corporateID: string,           // From URL param corpId
 *   corporateName: string,         // From URL param name  
 *   staffFirstName: string,        // Student first name
 *   staffLastName: string,         // Student last name
 *   classstandards: number,        // Numeric code (Grade 8 → 8)
 *   staffImage: string,            // Base64 image (empty for bulk)
 *   parentFirstName: string,       // Parent first name
 *   parentLastName: string,        // Parent last name
 *   parentsMobileNumber: string,   // Country code + digits (e.g., "254712345678")
 *   parentEmailAddress: string,    // Parent email
 *   address: string,               // Full address line
 *   pickupLL: string,              // "lat,lng" format - home location for morning pickup
 *   dropLL: string,                // "lat,lng" format - home location for afternoon drop
 *   staffMobileNumber: string,     // Empty for students
 *   area: string,                  // Area name
 *   city: string,                  // City name
 *   emailID: string,               // Always empty
 *   pickupStop: string,            // Pickup location name (home location)
 *   dropStop: string,              // Drop location name (home location)
 *   corporateType: string          // "school" for students, "corporate" for employees
 * }
 * 
 * @param {Object} rowData - Mapped row data from CSV
 * @param {string} type - 'Student' or 'Employee'
 * @param {string} corpId - Corporate ID from URL params
 * @param {string} corpName - Corporate Name from URL params
 * @returns {Object} - Transformed data matching API format
 */
export const transformBulkData = (rowData, type, corpId, corpName) => {
  // Validate required corporate info
  if (!corpId || !corpName) {
    throw new Error('Corporate ID and Corporate Name are required');
  }

  if (type !== 'Student') {
    throw new Error('This bulk upload is configured for Students only');
  }

  // Get country code (default to 254 for Kenya)
  const countryCode = rowData.countryCode || '254';

  // NEW: Use coordinates from Pickup From (home for morning) and Drop To (home for afternoon)
  // These were auto-filled during validation by the geo lookup
  const pickupFromLat = parseFloat(rowData.pickupFromLat);
  const pickupFromLng = parseFloat(rowData.pickupFromLng);
  const dropToLat = parseFloat(rowData.dropToLat);
  const dropToLng = parseFloat(rowData.dropToLng);

  if (isNaN(pickupFromLat) || isNaN(pickupFromLng)) {
    throw new Error(`Invalid Pickup From coordinates: lat=${rowData.pickupFromLat}, lng=${rowData.pickupFromLng}`);
  }
  if (isNaN(dropToLat) || isNaN(dropToLng)) {
    throw new Error(`Invalid Drop To coordinates: lat=${rowData.dropToLat}, lng=${rowData.dropToLng}`);
  }

  // Convert class to numeric code (same as individual registration)
  const classCode = CLASS_TO_CODE[rowData.class];
  if (classCode === undefined) {
    throw new Error(`Invalid class format: "${rowData.class}"`);
  }

  // Format mobile number with country code
  const parentMobileDigits = digitsOnly(rowData.parentMobile);
  const formattedMobile = countryCode + parentMobileDigits;

  // Build the EXACT API payload format (matching ReviewPage/index.js)
  // pickupLL = Pickup From location (student's home for morning pickup)
  // dropLL = Drop To location (student's home for afternoon drop)
  // pickupStop = The name of Pickup From location
  // dropStop = The name of Drop To location
  const apiPayload = {
    // Corporate identification
    corporateID: String(corpId),
    corporateName: String(corpName),
    
    // Student details (note: API uses "staff" prefix for historical reasons)
    staffFirstName: String(rowData.studentFirstName || '').trim(),
    staffLastName: String(rowData.studentLastName || '').trim(),
    classstandards: classCode,  // Numeric value
    staffImage: '',  // Empty for bulk upload (no images)
    
    // Parent details
    parentFirstName: String(rowData.parentFirstName || '').trim(),
    parentLastName: String(rowData.parentLastName || '').trim(),
    parentsMobileNumber: formattedMobile,
    parentEmailAddress: String(rowData.parentEmail || '').trim(),
    
    // Address details
    address: String(rowData.address || '').trim(),
    area: String(rowData.area || '').trim(),
    city: String(rowData.city || '').trim(),
    
    // Coordinates in "lat,lng" string format (matching individual registration)
    // pickupLL = coordinates of Pickup From (home location for morning pickup)
    // dropLL = coordinates of Drop To (home location for afternoon drop)
    pickupLL: `${pickupFromLat},${pickupFromLng}`,
    dropLL: `${dropToLat},${dropToLng}`,
    
    // Stop names: Use Pickup From and Drop To as the stop names
    // These represent where the bus picks up/drops off the student
    pickupStop: String(rowData.pickupFrom || '').trim(),
    dropStop: String(rowData.dropTo || '').trim(),
    
    // Staff mobile is empty for students
    staffMobileNumber: '',
    
    // Always empty
    emailID: '',
    
    // Type identifier
    corporateType: 'school'  // Always 'school' for students
  };

  // Log the transformed payload for debugging
  console.log('📤 Bulk Upload - Transformed API Payload:', JSON.stringify(apiPayload, null, 2));

  return apiPayload;
};
